// FireworksAnimator.js
import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';

class FireworksAnimator {
  constructor(canvas) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');
    this.rockets = [];
    this.particles = [];
    this.running = false;
    
    // Fireworks configuration
    this.rocketCount = 15; // Number of rockets to launch (3x more)
    this.rocketDelay = 200; // Milliseconds between rocket launches
    this.particleCount = 150; // Particles per explosion
    this.gravity = 0.09; // Slower gravity for realistic arc
    this.friction = 0.97; // Air resistance
    this.explosionRadius = 3; // Size multiplier for explosion
    this.hueBase = Math.random() * 360; // Random starting hue
    this.hueDrift = 20; // How much hue varies between rockets
    
    // Animation duration in milliseconds
    this.animationDuration = 5000;
    this.fadeOutStart = 4000; // Start fading out 1 second before end
    this.clearAlpha = 0.05; // Much lower clear alpha - barely visible trail effect
    
    // Fireworks colors - vibrant palette
    this.colorPalettes = [
      // Red/Gold palette
      ['#ff0000', '#ff5500', '#ffaa00', '#ffff00', '#ffddaa'],
      // Blue/Silver palette
      ['#0033ff', '#0099ff', '#00ddff', '#aaddff', '#ffffff'],
      // Green/Purple palette
      ['#00ff00', '#00dd99', '#aa00ff', '#ff00ff', '#ffaaff'],
      // Gold/White palette
      ['#ffdd00', '#ffaa00', '#ffcc88', '#ffffdd', '#ffffff'],
      // Mixed vibrant palette
      ['#ff0000', '#00ff00', '#0000ff', '#ffff00', '#ff00ff', '#00ffff', '#ffffff']
    ];

    // Bind methods to this instance
    this.update = this.update.bind(this);
    this.launchRocket = this.launchRocket.bind(this);
    this.createExplosion = this.createExplosion.bind(this);
  }

  randomInRange(min, max) {
    return Math.random() * (max - min) + min;
  }

  // Creates a rocket that will launch upward
  launchRocket() {
    if (!this.running) return;
    
    // Create 1-3 rockets at once for more density
    const rocketBatch = Math.floor(this.randomInRange(1, 4));
    
    for (let r = 0; r < rocketBatch; r++) {
      // Select a random color palette for this rocket
      const palette = this.colorPalettes[Math.floor(Math.random() * this.colorPalettes.length)];
      
      // Calculate a random hue variation for this rocket
      const hueVariation = this.hueBase + Math.random() * this.hueDrift * 2 - this.hueDrift;
      
      // Random horizontal position
      const x = this.randomInRange(0.1, 0.9) * this.canvas.width;
      
      // Start position at bottom of canvas
      const y = this.canvas.height;
      
      // Random upward velocity with horizontal variance
      const angle = this.randomInRange(-0.1, 0.1) + Math.PI / 2; // Mostly upward
      const velocity = this.randomInRange(10, 15);
      
      // Calculate velocity components
      const vx = Math.cos(angle) * velocity * (Math.random() > 0.5 ? 1 : -1) * 0.2;
      const vy = -Math.sin(angle) * velocity;
      
      // Create rocket particle
      this.rockets.push({
        x: x,
        y: y,
        vx: vx,
        vy: vy,
        radius: 2,
        color: palette[0], // Base color
        trailColor: palette[1], // Trail color
        palette: palette, // Store palette for explosion
        hue: hueVariation,
        targetHeight: this.canvas.height * this.randomInRange(0.15, 0.5), // Target height for explosion
        trail: [], // Store positions for trail effect
        maxTrailLength: 10, // Maximum length of the trail
        sparkTrail: true, // Whether to add spark effects to the trail
        glowing: true, // Glowing effect
        flickerRate: this.randomInRange(0.85, 0.95), // Rate at which the glow flickers
        flickerIntensity: this.randomInRange(0.3, 0.8), // How much the glow varies
        brightness: 1,
        createdAt: Date.now() // Track when this rocket was created
      });
    }
    
    // Calculate time elapsed
    const elapsed = Date.now() - this.startTime;
    
    // Determine if we should launch another rocket based on time elapsed
    if (this.running && this.rockets.length < this.rocketCount && elapsed < this.fadeOutStart) {
      // Schedule next rocket launch
      setTimeout(() => this.launchRocket(), this.rocketDelay);
    } else if (this.running && elapsed < this.fadeOutStart && Math.random() < 0.5) {
      // After initial burst, launch rockets with decreasing probability as we approach fadeOutStart
      const timeRatio = elapsed / this.fadeOutStart;
      const launchProbability = 1 - timeRatio * 0.8; // Gradually reduce probability
      
      if (Math.random() < launchProbability) {
        setTimeout(() => this.launchRocket(), this.rocketDelay * (1 + timeRatio)); // Gradually increase delay
      }
    }
  }

  // Create explosion particles when a rocket reaches its target height
  createExplosion(rocket) {
    // Create multiple particle sub-explosions for a more complex effect
    const subExplosions = Math.floor(this.randomInRange(1, 3));
    
    for (let s = 0; s < subExplosions; s++) {
      const offsetX = this.randomInRange(-10, 10);
      const offsetY = this.randomInRange(-10, 10);
      const delay = s * 100; // Stagger sub-explosions
      
      setTimeout(() => {
        const explosionType = Math.random();
        let particleCount = this.particleCount;
        
        // Calculate time elapsed - reduce particles if near end
        const elapsed = Date.now() - this.startTime;
        if (elapsed > this.fadeOutStart) {
          // Reduce particle count for explosions that happen during fade-out
          const fadeRatio = (elapsed - this.fadeOutStart) / (this.animationDuration - this.fadeOutStart);
          particleCount = Math.floor(particleCount * (1 - fadeRatio * 0.7));
        }
        
        // Different explosion types
        if (explosionType < 0.3) {
          // Standard circular explosion
          this.createCircularExplosion(rocket, offsetX, offsetY, particleCount);
        } else if (explosionType < 0.6) {
          // Heart-shaped explosion
          this.createPatternExplosion(rocket, offsetX, offsetY, particleCount, this.heartPattern);
        } else if (explosionType < 0.8) {
          // Ring explosion
          this.createRingExplosion(rocket, offsetX, offsetY, particleCount);
        } else {
          // Double-ring explosion
          this.createDoubleRingExplosion(rocket, offsetX, offsetY, particleCount);
        }
        
        // Add a smaller, secondary delayed explosion
        if (Math.random() < 0.5 && elapsed < this.fadeOutStart + 500) {
          setTimeout(() => {
            this.createCircularExplosion(rocket, offsetX, offsetY, particleCount / 3, 0.6);
          }, 200);
        }
      }, delay);
    }
  }

  // Create a standard circular explosion pattern
  createCircularExplosion(rocket, offsetX, offsetY, count, sizeMultiplier = 1) {
    const palette = rocket.palette;
    const newParticles = [];
    
    for (let i = 0; i < count; i++) {
      // Create particles in all directions (full 360-degree explosion)
      const angle = this.randomInRange(0, Math.PI * 2);
      const velocity = this.randomInRange(1, 5) * this.explosionRadius * sizeMultiplier;
      
      // Randomize colors from the palette
      const color = palette[Math.floor(Math.random() * palette.length)];
      
      // Create core explosion particle
      newParticles.push(this.createParticle(
        rocket.x + offsetX,
        rocket.y + offsetY,
        angle,
        velocity,
        color,
        rocket.hue
      ));
    }
    
    this.particles = [...this.particles, ...newParticles];
  }
  
  // Create a ring-shaped explosion
  createRingExplosion(rocket, offsetX, offsetY, count) {
    const palette = rocket.palette;
    const newParticles = [];
    
    for (let i = 0; i < count; i++) {
      // Create particles in a circle
      const angle = (i / count) * Math.PI * 2;
      const velocity = this.randomInRange(2, 4) * this.explosionRadius;
      
      // Alternate colors for ring effect
      const colorIndex = i % palette.length;
      const color = palette[colorIndex];
      
      newParticles.push(this.createParticle(
        rocket.x + offsetX,
        rocket.y + offsetY,
        angle,
        velocity,
        color,
        rocket.hue
      ));
    }
    
    this.particles = [...this.particles, ...newParticles];
  }
  
  // Create a double-ring explosion
  createDoubleRingExplosion(rocket, offsetX, offsetY, count) {
    const palette = rocket.palette;
    const newParticles = [];
    
    // Inner ring
    for (let i = 0; i < count * 0.7; i++) {
      const angle = (i / (count * 0.7)) * Math.PI * 2;
      const velocity = this.randomInRange(1, 2) * this.explosionRadius;
      const color = palette[i % palette.length];
      
      newParticles.push(this.createParticle(
        rocket.x + offsetX,
        rocket.y + offsetY,
        angle,
        velocity,
        color,
        rocket.hue
      ));
    }
    
    // Outer ring
    for (let i = 0; i < count * 0.5; i++) {
      const angle = (i / (count * 0.5)) * Math.PI * 2;
      const velocity = this.randomInRange(3, 4) * this.explosionRadius;
      const color = palette[(i + 2) % palette.length];
      
      newParticles.push(this.createParticle(
        rocket.x + offsetX,
        rocket.y + offsetY,
        angle,
        velocity,
        color,
        rocket.hue
      ));
    }
    
    this.particles = [...this.particles, ...newParticles];
  }
  
  // Create a heart-shaped pattern (or other patterns)
  createPatternExplosion(rocket, offsetX, offsetY, count, patternFunc) {
    const palette = rocket.palette;
    const newParticles = [];
    
    for (let i = 0; i < count; i++) {
      // Map i to angle from 0 to 2*PI
      const t = (i / count) * Math.PI * 2;
      
      // Get heart coordinates from pattern function
      const coords = this.heartPattern(t);
      
      // Calculate angle and velocity to reach those coordinates
      const angle = Math.atan2(coords.y, coords.x);
      const distance = Math.sqrt(coords.x * coords.x + coords.y * coords.y);
      const velocity = distance * this.explosionRadius * 0.8;
      
      // Alternate colors
      const color = palette[i % palette.length];
      
      newParticles.push(this.createParticle(
        rocket.x + offsetX,
        rocket.y + offsetY,
        angle,
        velocity,
        color,
        rocket.hue
      ));
    }
    
    this.particles = [...this.particles, ...newParticles];
  }
  
  // Heart pattern function
  heartPattern(t) {
    // Parametric equation for a heart
    const x = 16 * Math.pow(Math.sin(t), 3);
    const y = 13 * Math.cos(t) - 5 * Math.cos(2*t) - 2 * Math.cos(3*t) - Math.cos(4*t);
    return { x: x * 0.03, y: -y * 0.03 }; // Scale down
  }
  
  // Helper function to create a particle with common properties
  createParticle(x, y, angle, velocity, color, hue) {
    // Determine if this particle will be a special effect
    const isSparkler = Math.random() < 0.3; // 30% chance
    const isTracer = Math.random() < 0.1; // 10% chance for trailing effect
    
    // Properties for realistic movement
    const gravity = this.gravity * (1 + this.randomInRange(-0.1, 0.1));
    const friction = this.friction * (1 + this.randomInRange(-0.01, 0.01));
    
    // Calculate time elapsed - adjust decay based on animation progress
    const elapsed = Date.now() - this.startTime;
    let decayMultiplier = 1;
    
    // Increase decay rate for particles created during fade-out phase
    if (elapsed > this.fadeOutStart) {
      const fadeRatio = (elapsed - this.fadeOutStart) / (this.animationDuration - this.fadeOutStart);
      decayMultiplier = 1 + fadeRatio * 2; // Up to 3x faster decay during fade-out
    }
    
    return {
      x: x,
      y: y,
      vx: Math.cos(angle) * velocity,
      vy: Math.sin(angle) * velocity,
      radius: isSparkler ? this.randomInRange(1, 2) : this.randomInRange(1.5, 3),
      color: color,
      hue: hue + this.randomInRange(-20, 20), // Slight hue variation
      gravity: gravity,
      friction: friction,
      life: 1,
      decay: this.randomInRange(0.01, 0.02) * decayMultiplier, // Adjust decay rate based on animation time
      alpha: 1,
      isSparkler: isSparkler,
      isTracer: isTracer,
      trail: [],
      maxTrailLength: isTracer ? 8 : 0,
      // Shimmer effect parameters
      shimmer: 0,
      shimmerSpeed: this.randomInRange(0.03, 0.08),
      shimmerDirection: Math.random() > 0.5 ? 1 : -1,
      shimmerAmplitude: this.randomInRange(0.2, 0.4),
      // Rotation for non-circular particles
      rotation: this.randomInRange(0, Math.PI * 2),
      spin: this.randomInRange(-0.1, 0.1),
      createdAt: Date.now() // Track when this particle was created
    };
  }
  
  update() {
    const { ctx, canvas } = this;
    
    // Calculate time elapsed since animation started
    const elapsed = Date.now() - this.startTime;
    
    // Clear the canvas with transparency to create trail effect
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Instead of filling with black, we create a transparent overlay for trails
    if (elapsed <= this.fadeOutStart) {
      // Create a temporary canvas for the fading effect
      if (!this.trailCanvas) {
        this.trailCanvas = document.createElement('canvas');
        this.trailCanvas.width = canvas.width;
        this.trailCanvas.height = canvas.height;
        this.trailCtx = this.trailCanvas.getContext('2d');
        
        // Copy the current canvas to the trail canvas once
        this.trailCtx.drawImage(canvas, 0, 0);
      }
      
      // Apply fading to the trail canvas
      this.trailCtx.fillStyle = `rgba(0, 0, 0, ${this.clearAlpha})`;
      this.trailCtx.globalCompositeOperation = 'destination-out';
      this.trailCtx.fillRect(0, 0, this.trailCanvas.width, this.trailCanvas.height);
      this.trailCtx.globalCompositeOperation = 'source-over';
      
      // Draw the faded trails back to the main canvas
      ctx.globalAlpha = 0.9;
      ctx.drawImage(this.trailCanvas, 0, 0);
      ctx.globalAlpha = 1.0;
    }
    
    // Update and draw rockets
    for (let i = this.rockets.length - 1; i >= 0; i--) {
      const rocket = this.rockets[i];
      
      // Update position
      rocket.x += rocket.vx;
      rocket.y += rocket.vy;
      
      // Apply gravity
      rocket.vy += this.gravity;
      
      // Store trail positions
      rocket.trail.unshift({ x: rocket.x, y: rocket.y });
      if (rocket.trail.length > rocket.maxTrailLength) {
        rocket.trail.pop();
      }
      
      // Update rocket flicker effect
      rocket.brightness = Math.max(0.6, Math.min(1, rocket.brightness * (rocket.flickerRate + (1 - rocket.flickerRate) * Math.random() * rocket.flickerIntensity)));
      
      // Apply fade-out effect if in fade-out phase
      let rocketAlpha = 1;
      const rocketElapsed = Date.now() - rocket.createdAt;
      if (elapsed > this.fadeOutStart) {
        // Calculate fade ratio
        const fadeRatio = (elapsed - this.fadeOutStart) / (this.animationDuration - this.fadeOutStart);
        rocketAlpha = Math.max(0, 1 - fadeRatio);
      }
      
      // Draw rocket
      ctx.save();
      
      // Glow effect
      if (rocket.glowing) {
        ctx.globalCompositeOperation = 'lighter';
        ctx.beginPath();
        const gradient = ctx.createRadialGradient(
          rocket.x, rocket.y, 0,
          rocket.x, rocket.y, rocket.radius * 8
        );
        gradient.addColorStop(0, `rgba(255, 220, 100, ${0.3 * rocket.brightness * rocketAlpha})`);
        gradient.addColorStop(0.1, `rgba(255, 220, 100, ${0.1 * rocket.brightness * rocketAlpha})`);
        gradient.addColorStop(1, 'rgba(255, 220, 100, 0)');
        
        ctx.fillStyle = gradient;
        ctx.arc(rocket.x, rocket.y, rocket.radius * 8, 0, Math.PI * 2);
        ctx.fill();
      }
      
      // Draw rocket trail
      if (rocket.trail.length > 1) {
        ctx.globalCompositeOperation = 'lighter';
        for (let t = 0; t < rocket.trail.length - 1; t++) {
          const point = rocket.trail[t];
          const nextPoint = rocket.trail[t + 1];
          
          // Draw trail segment
          ctx.strokeStyle = rocket.trailColor;
          ctx.lineWidth = Math.max(0, (rocket.maxTrailLength - t) / rocket.maxTrailLength * 2);
          ctx.globalAlpha = Math.max(0, (rocket.maxTrailLength - t) / rocket.maxTrailLength * 0.6 * rocketAlpha);
          
          ctx.beginPath();
          ctx.moveTo(point.x, point.y);
          ctx.lineTo(nextPoint.x, nextPoint.y);
          ctx.stroke();
          
          // Add occasional sparks to trail
          if (rocket.sparkTrail && Math.random() < 0.3 && t < 2) {
            ctx.globalAlpha = Math.random() * 0.7 * rocketAlpha;
            ctx.beginPath();
            const sparkX = point.x + this.randomInRange(-3, 3);
            const sparkY = point.y + this.randomInRange(-3, 3);
            ctx.arc(sparkX, sparkY, this.randomInRange(0.5, 1), 0, Math.PI * 2);
            ctx.fillStyle = rocket.trailColor;
            ctx.fill();
          }
        }
      }
      
      // Draw rocket core
      ctx.globalCompositeOperation = 'source-over';
      ctx.globalAlpha = rocket.brightness * rocketAlpha;
      ctx.fillStyle = rocket.color;
      ctx.beginPath();
      ctx.arc(rocket.x, rocket.y, rocket.radius, 0, Math.PI * 2);
      ctx.fill();
      
      ctx.restore();
      
      // Check if reached target height for explosion
      if (rocket.vy >= 0 && rocket.y <= rocket.targetHeight) {
        // Create explosion
        this.createExplosion(rocket);
        // Remove the rocket
        this.rockets.splice(i, 1);
      }
      
      // Also remove if it goes off screen
      if (rocket.y > canvas.height || rocket.x < 0 || rocket.x > canvas.width) {
        this.rockets.splice(i, 1);
      }
      
      // Apply extra fade-out effect near the end of animation
      if (elapsed > this.fadeOutStart + (this.animationDuration - this.fadeOutStart) * 0.7) {
        // Increase rocket decay rate in the last 30% of fade-out
        rocket.vy += this.gravity * 0.5; // Extra gravity
        if (Math.random() < 0.1) {
          this.rockets.splice(i, 1); // Randomly remove rockets
        }
      }
    }
    
    // Update and draw particles
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];
      
      // Update position with friction
      p.x += p.vx;
      p.y += p.vy;
      p.vx *= p.friction;
      p.vy *= p.friction;
      p.vy += p.gravity;
      
      // Update trail if it's a tracer
      if (p.isTracer && p.trail.length > 0) {
        p.trail.unshift({ x: p.x, y: p.y });
        if (p.trail.length > p.maxTrailLength) {
          p.trail.pop();
        }
      }
      
      // Update life and shimmer effect
      p.life -= p.decay;
      p.shimmer += p.shimmerSpeed * p.shimmerDirection;
      if (Math.abs(p.shimmer) > p.shimmerAmplitude) {
        p.shimmerDirection *= -1;
      }
      
      // Update rotation
      p.rotation += p.spin;
      
      // Apply fade-out effect if in fade-out phase
      let extraFade = 1;
      if (elapsed > this.fadeOutStart) {
        // Calculate fade ratio
        const fadeRatio = (elapsed - this.fadeOutStart) / (this.animationDuration - this.fadeOutStart);
        // Apply faster fade to particles during fade-out phase
        extraFade = Math.max(0, 1 - fadeRatio * 1.3);
        
        // Increase decay rate during fade-out
        p.decay += 0.001 * fadeRatio * 5;
      }
      
      // Draw particle
      ctx.save();
      
      // Set composite operation for glow effect
      if (p.isSparkler) {
        ctx.globalCompositeOperation = 'lighter';
      } else {
        ctx.globalCompositeOperation = 'source-over';
      }
      
      // Calculate alpha with shimmer and life, adjusted for fade-out
      const baseAlpha = p.life * extraFade;
      const shimmerEffect = p.shimmer * 0.3;
      ctx.globalAlpha = Math.max(0, Math.min(1, baseAlpha + shimmerEffect));
      
      // Draw tracer trail
      if (p.isTracer && p.trail.length > 1) {
        ctx.globalCompositeOperation = 'lighter';
        for (let t = 0; t < p.trail.length - 1; t++) {
          const point = p.trail[t];
          const nextPoint = p.trail[t + 1];
          
          ctx.strokeStyle = p.color;
          ctx.lineWidth = (p.maxTrailLength - t) / p.maxTrailLength * p.radius;
          ctx.globalAlpha = (p.maxTrailLength - t) / p.maxTrailLength * 0.5 * p.life * extraFade;
          
          ctx.beginPath();
          ctx.moveTo(point.x, point.y);
          ctx.lineTo(nextPoint.x, nextPoint.y);
          ctx.stroke();
        }
      }
      
      // Draw the particle
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rotation);
      
      if (p.isSparkler) {
        // Sparklers have a point-like glow
        ctx.beginPath();
        
        // Inner bright core
        const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, p.radius * 4);
        gradient.addColorStop(0, p.color);
        gradient.addColorStop(0.2, p.color);
        gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
        
        ctx.fillStyle = gradient;
        ctx.arc(0, 0, p.radius * 4, 0, Math.PI * 2);
        ctx.fill();
        
        // Extra bright center
        ctx.beginPath();
        ctx.arc(0, 0, p.radius, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
        ctx.fill();
      } else {
        // Standard particles are drawn as small rectangles or circles
        if (Math.random() < 0.7) {
          // Circles
          ctx.beginPath();
          ctx.arc(0, 0, p.radius, 0, Math.PI * 2);
          ctx.fillStyle = p.color;
          ctx.fill();
        } else {
          // Rectangles for variety
          const width = p.radius * 2;
          const height = p.radius;
          ctx.fillStyle = p.color;
          ctx.fillRect(-width/2, -height/2, width, height);
        }
      }
      
      ctx.restore();
      
      // Remove particles that have faded out or gone off screen
      if (p.life <= 0 || p.y > canvas.height || p.x < -50 || p.x > canvas.width + 50) {
        this.particles.splice(i, 1);
      }
      
      // Apply extra fade-out effect near the end of animation
      if (elapsed > this.fadeOutStart + (this.animationDuration - this.fadeOutStart) * 0.8) {
        // Increase particle decay rate in the last 20% of fade-out
        p.life -= 0.02; // Extra life reduction
        if (Math.random() < 0.05) {
          this.particles.splice(i, 1); // Randomly remove some particles
        }
      }
    }
    
    // Continue animation if there are still elements to animate and we're running
    if ((this.rockets.length > 0 || this.particles.length > 0) && this.running) {
      requestAnimationFrame(this.update);
    } else if (this.running) {
      // Check if we should continue launching rockets
      if (elapsed < this.animationDuration && this.running) {
        // Only launch new rockets if we're not in fade-out phase
        if (elapsed < this.fadeOutStart) {
          this.launchRocket();
        }
        requestAnimationFrame(this.update);
      } else {
        // Gradually clear the canvas as the final step
        if (elapsed >= this.animationDuration) {
          this.fadeOutAndStop();
        } else {
          requestAnimationFrame(this.update);
        }
      }
    }
  }
  
  // Final fade out sequence
  fadeOutAndStop() {
    const { ctx, canvas } = this;
    
    // Just clear the canvas completely and stop
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Clean up temporary canvas if it exists
    if (this.trailCanvas) {
      this.trailCanvas = null;
      this.trailCtx = null;
    }
    
    this.stop();
  }

  start() {
    // Skip if animation is not supported or canvas is not available
    if (typeof requestAnimationFrame !== 'function' || !this.canvas) {
      return false;
    }
    
    // Ensure canvas has proper dimensions
    if (this.canvas.width === 0 || this.canvas.height === 0) {
      this.canvas.width = this.canvas.parentElement.clientWidth || window.innerWidth;
      this.canvas.height = this.canvas.parentElement.clientHeight || window.innerHeight;
    }
    
    // Reset animation state
    this.rockets = [];
    this.particles = [];
    this.running = true;
    this.startTime = Date.now();
    
    // Clear any previous trail canvas
    this.trailCanvas = null;
    this.trailCtx = null;
    
    // Launch first rocket
    this.launchRocket();
    
    // Start animation loop
    this.update();
    
    // Auto-stop after duration
    setTimeout(() => {
      this.running = false;
    }, this.animationDuration + 500); // Add 500ms to allow for fade-out
    
    return true;
  }

  stop() {
    this.running = false;
    if (this.ctx) {
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    }
    
    // Clean up temporary canvas if it exists
    if (this.trailCanvas) {
      this.trailCanvas = null;
      this.trailCtx = null;
    }
  }
}

// React component that uses the FireworksAnimator
const Fireworks = ({ trigger = false }) => {
  const canvasRef = useRef(null);
  const animatorRef = useRef(null);
  const [showBanner, setShowBanner] = useState(false);
  const [initialized, setInitialized] = useState(false);

  // Initialize canvas and animator
  useEffect(() => {
    const canvas = canvasRef.current;
    
    if (!canvas) return;
    
    // Set canvas size to match parent element
    const resizeCanvas = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      
      // Update both HTML size attributes AND style dimensions
      canvas.width = parent.clientWidth || window.innerWidth;
      canvas.height = parent.clientHeight || window.innerHeight;
      
      // If animator exists, resize properly
      if (animatorRef.current && animatorRef.current.running) {
        const wasRunning = animatorRef.current.running;
        if (wasRunning) animatorRef.current.stop();
        if (wasRunning) animatorRef.current.start();
      }
    };
    
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    
    // Initialize the fireworks animator
    animatorRef.current = new FireworksAnimator(canvas);
    setInitialized(true);
    
    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (animatorRef.current) {
        animatorRef.current.stop();
      }
    };
  }, []);
  
  // Trigger fireworks animation when the trigger prop changes to true
  useEffect(() => {
    if (trigger && animatorRef.current && initialized) {
      // Start the fireworks animation
      animatorRef.current.start();
      
      // Show and then hide the banner
      setShowBanner(true);
      
      // Hide banner after animation completes
      const timer = setTimeout(() => {
        setShowBanner(false);
      }, 4500); // Slightly shorter than animation duration
      
      return () => clearTimeout(timer);
    }
  }, [trigger, initialized]);

  return (
    <>
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          zIndex: 1000,
        }}
      >
        <canvas 
          ref={canvasRef} 
          style={{ 
            width: '100%', 
            height: '100%',
            display: 'block', 
          }} 
        />
      </Box>
      
      {/* Celebration Banner (same as in ConfettiAnimator) */}
      {showBanner && (
        <Box
          sx={{
            position: 'absolute',
            top: '40%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 1001,
            textAlign: 'center',
            animation: 'fadeInOut 5s ease-in-out',
            '@keyframes fadeInOut': {
              '0%': { opacity: 0, transform: 'translate(-50%, -60%) scale(0.8)' },
              '20%': { opacity: 1, transform: 'translate(-50%, -50%) scale(1.05)' },
              '25%': { transform: 'translate(-50%, -50%) scale(1)' },
              '85%': { opacity: 1, transform: 'translate(-50%, -50%)' },
              '100%': { opacity: 0, transform: 'translate(-50%, -45%)' }
            },
            padding: '22px 40px',
            borderRadius: '12px',
            background: 'linear-gradient(135deg, rgba(220, 240, 255, 0.9), rgba(40, 120, 220, 0.85))',
            boxShadow: '0 6px 25px rgba(0,50,120,0.25), 0 0 15px rgba(255,255,255,0.6) inset',
            border: '1px solid rgba(255, 255, 255, 0.5)',
            width: { xs: '80%', sm: 'auto' },
            maxWidth: '90vw',
          }}
        >
          <Typography
            variant="h4"
            component="h2"
            sx={{
              fontWeight: '600',
              color: 'white',
              textShadow: '0 2px 4px rgba(0,40,100,0.35)',
              letterSpacing: '0.5px',
              fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' },
              fontFamily: '"Segoe UI", Roboto, "Helvetica Neue", sans-serif',
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: '-8px',
                left: '10%',
                width: '80%',
                height: '2px',
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.8), transparent)',
              }
            }}
          >
            Today's Goal Completed!
          </Typography>
        </Box>
      )}
    </>
  );
};

export { Fireworks };